package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * tooltip 内容引用的解析期 AST。
 */
sealed interface TooltipContentAst : AstNode {

    /** 字符串形态。 */
    data class Text(
        val value: ExprAst,
        override val position: SourcePosition,
    ) : TooltipContentAst

    /** 内联子树形态。 */
    data class Inline(
        val root: ElementAst,
        override val position: SourcePosition,
        /**
         * ADR-0036:tooltip 专用异步数据请求绑定（0..N）。
         *
         * 每条绑定来自块顶部的 `data NAME = net.tooltip("ns.handler", #{...})` 声明。
         * 运行时在 overlay 挂载时按 hover 上下文求 payload、查客户端缓存 / 发请求，把结果作为
         * 同名 local 句柄注入（`NAME.ready` / `.loading` / `.error` / `.value`），复用 source 形参的
         * IdentRef → locals 解析路径。每帧重建天然刷新，无需重挂载。
         */
        val dataBindings: List<TooltipDataBinding> = emptyList(),
    ) : TooltipContentAst

    /** Component 引用形态。 */
    data class Component(
        val name: String,
        val props: List<ElementAst.PropAssign>,
        override val position: SourcePosition,
    ) : TooltipContentAst
}

/**
 * ADR-0036:tooltip 块内 `data NAME = net.tooltip("ns.handler", #{...})` 数据请求绑定。
 *
 * - [name]：句柄名，运行时注入为同名 local（如 `setInfo`），通过 `setInfo.ready` 等字段读取。
 * - [namespace] / [handler]：从 channel 字面量 `"ns.handler"` 拆分（必须含 '.'）。
 * - [payload]：请求参数 Map 字面量条目；表达式只能读 hover 上下文 / slot.read，禁读 screen.state（E730）。
 */
data class TooltipDataBinding(
    val name: String,
    val namespace: String,
    val handler: String,
    val payload: List<ExprAst.NetPayloadEntry>,
    val position: SourcePosition,
    /**
     * ADR-0036：可选条件门控（`net.tooltip(...) when <expr>`）。
     *
     * 为假时挂载期跳过请求（不发包），句柄 ready / loading / error 全为 false——用于「物品有某 NBT 才请求」
     * 等场景，避免无关物品也发请求。表达式同 payload 约束（可读 slot.read，禁读 screen.state）。null 表示无条件。
     */
    val condition: ExprAst? = null,
)
